import { getToken } from "./auth";

/* The calendar service (calender.cilpron.com): the same API your full Calendar screen uses.
 * One sign-in serves both: the suite's token is sent as a Bearer token. */

export const CAL_BASE_URL = ((import.meta.env?.VITE_CALENDAR_URL as string | undefined) ?? "https://calender.cilpron.com").replace(/\/+$/, "");

export const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Lagos";
  } catch {
    return "Africa/Lagos";
  }
})();

export type Repeat = "none" | "daily" | "weekdays" | "weekly" | "monthly" | "yearly" | "custom";
export const REPEATS: Repeat[] = ["none", "daily", "weekdays", "weekly", "monthly", "yearly", "custom"];

export interface Guest {
  name: string;
  email: string;
  userId: string | null;
}

export interface CalDef {
  id: string;
  name: string;
  color: string;
  canEdit: boolean;
}

/** One occurrence of an event. Times are minutes from local midnight. */
export interface CalEv {
  id: string;
  calId: string;
  title: string;
  date: string; // yyyy-mm-dd of THIS occurrence
  seriesDate: string;
  startAt: string; // ISO start of this occurrence
  start: number;
  end: number;
  allDay: boolean;
  repeat: Repeat;
  recurring: boolean;
  location: string;
  online: boolean;
  onlineProvider: string;
  onlineUrl: string;
  organizer: string;
  mine: boolean;
  guests: Guest[];
  notes: string;
  canEdit: boolean;
}

export class CalendarError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "CalendarError";
  }
}

export const calendarMessage = (e: unknown): string => (e instanceof CalendarError ? e.message : "Something went wrong. Try again.");

const readToken = (): string | null => {
  const t = getToken();
  if (t) return t;
  try {
    return window.localStorage.getItem("authToken");
  } catch {
    return null;
  }
};

/** The signed-in person's id in the token (`sub`), only used to label "You" as organizer. Never for security. */
export const tokenSub = (token: string | null): string | undefined => {
  try {
    if (!token) return undefined;
    const p = token.split(".")[1] ?? "";
    const bin = atob(p.replace(/-/g, "+").replace(/_/g, "/"));
    const json = decodeURIComponent(
      bin
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const o = JSON.parse(json) as Record<string, string>;
    return (o.sub ?? o.nameid ?? o["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] ?? o.userId)?.toString().toLowerCase();
  } catch {
    return undefined;
  }
};

const enc = encodeURIComponent;

async function call<T>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    res = await fetch(`${CAL_BASE_URL}/api/calendar${path}`, init);
  } catch {
    throw new CalendarError(0, "Couldn’t reach the calendar. Check your connection.");
  }
  if (res.status === 401) throw new CalendarError(401, "Your session ended. Sign in again.");
  if (res.status === 403) throw new CalendarError(403, "You don’t have permission to do that.");
  if (!res.ok) {
    let msg = "";
    try {
      const j = (await res.json()) as Record<string, unknown>;
      const pick = j.detail ?? j.message ?? j.title ?? j.error;
      if (typeof pick === "string") msg = pick;
    } catch {
      /* no readable body */
    }
    throw new CalendarError(res.status, res.status === 400 ? msg || "That could not be saved. Check the entries and try again." : msg || `The calendar answered with an error (${res.status}).`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/* ---------- wire types ---------- */
interface ApiGuest {
  name?: string | null;
  email?: string | null;
  userId?: string | null;
}
export interface ApiEvent {
  id: string;
  calendarId: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startAt: string;
  endAt: string;
  seriesStartAt?: string | null;
  isAllDay: boolean;
  repeat?: string;
  isRecurring: boolean;
  organizerName?: string | null;
  createdBy?: string;
  onlineProvider?: string | null;
  onlineUrl?: string | null;
  canEdit: boolean;
  attendees?: ApiGuest[];
}
interface ApiCalendar {
  id: string;
  name: string;
  color?: string | null;
  role?: string;
}

/* ---------- date helpers ---------- */
const pad = (n: number): string => String(n).padStart(2, "0");
export const ymd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseYmd = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};
export const hhmm = (m: number): string => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
export const toMin = (s: string): number => {
  const [h, m] = s.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const minutesOf = (d: Date): number => d.getHours() * 60 + d.getMinutes();

const SWATCHES = ["#0f6cbd", "#00a858", "#f7630c", "#8764b8", "#c4314b", "#038387"];

/** The provider name this app writes for a Spurt meeting. The calendar service stores it as given. */
export const SPURT_PROVIDER = "Spurt";

export const toEv = (a: ApiEvent, me: string | undefined): CalEv => {
  const s = new Date(a.startAt);
  const e = new Date(a.endAt);
  const date = a.isAllDay ? a.startAt.slice(0, 10) : ymd(s);
  const start = a.isAllDay ? 0 : minutesOf(s);
  let end = a.isAllDay ? 24 * 60 : ymd(e) === date ? minutesOf(e) : 24 * 60;
  if (end <= start) end = Math.min(start + 15, 24 * 60);
  const series = a.seriesStartAt ? new Date(a.seriesStartAt) : s;
  const provider = (a.onlineProvider ?? "none").trim();
  const online = provider !== "" && provider.toLowerCase() !== "none";
  const guests: Guest[] = (a.attendees ?? [])
    .map((g) => ({ name: (g.name ?? "").trim(), email: (g.email ?? "").trim(), userId: g.userId ?? null }))
    .filter((g) => g.name || g.email);
  const repeat = (REPEATS.includes(a.repeat as Repeat) ? a.repeat : a.isRecurring ? "custom" : "none") as Repeat;
  const mine = !!me && (a.createdBy ?? "").toLowerCase() === me;
  return {
    id: a.id,
    calId: a.calendarId,
    title: a.title,
    date,
    seriesDate: a.isAllDay ? (a.seriesStartAt ?? a.startAt).slice(0, 10) : ymd(series),
    startAt: a.startAt,
    start,
    end,
    allDay: a.isAllDay,
    repeat,
    recurring: a.isRecurring,
    location: a.location ?? "",
    online,
    onlineProvider: provider,
    onlineUrl: a.onlineUrl ?? "",
    organizer: mine ? "You" : a.organizerName || "Organizer",
    mine,
    guests,
    notes: a.description ?? "",
    canEdit: a.canEdit,
  };
};

const toCal = (c: ApiCalendar, i: number): CalDef => ({
  id: c.id,
  name: c.name,
  color: c.color || SWATCHES[i % SWATCHES.length] || "#0f6cbd",
  canEdit: ["owner", "manager", "editor"].includes((c.role ?? "").toLowerCase()),
});

/* ---------- operations ---------- */
export interface Feed {
  calendars: CalDef[];
  events: CalEv[];
}

/** Everything in [from, to). The feed is asked first: a new person gets a default calendar there. */
export async function loadFeed(from: Date, to: Date): Promise<Feed> {
  const feed = await call<ApiEvent[]>("GET", `/feed?from=${enc(from.toISOString())}&to=${enc(to.toISOString())}`);
  const list = await call<ApiCalendar[]>("GET", "/calendars");
  const me = tokenSub(readToken());
  return { calendars: list.map(toCal), events: feed.map((a) => toEv(a, me)) };
}

export interface EventInput {
  calendarId: string;
  title: string;
  description: string;
  location: string;
  date: string; // yyyy-mm-dd
  start: string; // HH:mm
  end: string; // HH:mm
  guests: Guest[];
  /** "" = no online meeting; otherwise the provider name (SPURT_PROVIDER) */
  onlineProvider: string;
  /** set when a Spurt meeting has been created for this event */
  onlineUrl?: string;
  /** only sent when it should change the repeat */
  recurrence?: Repeat;
}

const bodyOf = (v: EventInput): Record<string, unknown> => {
  const day = parseYmd(v.date);
  const at = (m: number) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, m).toISOString();
  const body: Record<string, unknown> = {
    calendarId: v.calendarId,
    title: v.title,
    description: v.description,
    location: v.location,
    startAt: at(toMin(v.start)),
    endAt: at(toMin(v.end)),
    isAllDay: false,
    timeZone: TZ,
    onlineProvider: v.onlineProvider || "None",
    attendees: v.guests.map((g) => ({ name: g.name, email: g.email, ...(g.userId ? { userId: g.userId } : {}) })),
  };
  if (v.onlineUrl) body.onlineUrl = v.onlineUrl;
  if (v.recurrence) body.recurrence = v.recurrence;
  return body;
};

export async function createEvent(v: EventInput): Promise<ApiEvent | undefined> {
  return call<ApiEvent | undefined>("POST", "/events", bodyOf(v));
}

export async function updateEvent(id: string, v: EventInput): Promise<void> {
  await call("PATCH", `/events/${enc(id)}?scope=all`, bodyOf(v));
}

export async function deleteEvent(ev: CalEv, scope: "this" | "all"): Promise<void> {
  if (scope === "this") await call("DELETE", `/events/${enc(ev.id)}?scope=this&occurrence=${enc(ev.startAt)}`);
  else await call("DELETE", `/events/${enc(ev.id)}?undo=true`);
}

export async function restoreEvent(id: string): Promise<void> {
  await call("POST", `/events/${enc(id)}/restore`);
}
